import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bell, PlusCircle, RefreshCw, Package, CheckCircle2, Banknote, ArrowRight } from "lucide-react";
import { notificationsAPI } from "../api";
import { useAuth } from "../context/AuthContext";
import "./NotificationsPage.css";

const NotificationsPage = () => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAuthenticated) return;

    const loadNotifications = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await notificationsAPI.getAll({ limit: 100 });
        setNotifications(response.data);
      } catch (err) {
        setError("Erreur lors du chargement des notifications");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, [isAuthenticated]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        )
      );
    } catch (err) {
      console.error("Erreur lors du marquage comme lu:", err);
      setError("Impossible de marquer cette notification comme lue. Réessayez.");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, is_read: true }))
      );
    } catch (err) {
      console.error("Erreur lors du marquage de toutes les notifications:", err);
      setError("Impossible de marquer les notifications comme lues. Réessayez.");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getNotificationIcon = (type) => {
    const icons = {
      order_created: PlusCircle,
      order_status_changed: RefreshCw,
      order_shipped: Package,
      order_delivered: CheckCircle2,
      payment_received: Banknote,
    };
    const Icon = icons[type] || Bell;
    return <Icon size={22} strokeWidth={2} />;
  };

  if (!isAuthenticated) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">
          Vous devez être connecté pour voir vos notifications
        </div>
        <Link to="/login" className="btn btn-primary">
          Se connecter
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="container main-content">
      <div className="page-header">
        <h1 className="icon-title"><Bell size={26} strokeWidth={2.25} /> Notifications</h1>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="btn btn-outline btn-sm"
          >
            Tout marquer comme lu ({unreadCount})
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      {notifications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Bell size={56} strokeWidth={1.75} /></div>
          <h3>Aucune notification</h3>
          <p>Vous n'avez pas encore de notifications.</p>
        </div>
      ) : (
        <div className="notifications-list">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-card ${!notification.is_read ? "notification-unread" : ""}`}
              onClick={() => {
                if (!notification.is_read) {
                  handleMarkAsRead(notification.id);
                }
                if (notification.order_id) {
                  window.location.href = `/orders/${notification.order_id}`;
                }
              }}
            >
              <div className="notification-icon">
                {getNotificationIcon(notification.notification_type)}
              </div>
              <div className="notification-content">
                <div className="notification-header">
                  <h3 className="notification-title">{notification.title}</h3>
                  {!notification.is_read && (
                    <span className="notification-badge">Nouveau</span>
                  )}
                </div>
                <p className="notification-message">{notification.message}</p>
                <div className="notification-footer">
                  <span className="notification-date">
                    {formatDate(notification.created_at)}
                  </span>
                  {notification.order_id && (
                    <Link
                      to={`/orders/${notification.order_id}`}
                      className="notification-link icon-inline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Voir la commande <ArrowRight size={13} strokeWidth={2.25} />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;

